/**
 * Donation Utilities
 * 
 * This module provides standardized utilities for creating and managing donation records
 * consistently across different payment flows.
 * 
 * SECURITY NOTE: This module adheres to PCI DSS compliance requirements.
 * - Never handle, process, or store raw credit card data
 * - Only store tokenized payment information
 * - Always mask sensitive data in logs and UI
 * - Maintain comprehensive audit trails
 */

import {
  serverTimestamp,
  addDoc,
  collection,
  query,
  where,
  getDocs,
  getDoc,
  doc,
  updateDoc,
  writeBatch
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { Donation, DonationSettings, CompanySettings, DynamicField } from '@/types/firebase';
import { v4 as uuidv4 } from 'uuid';

/**
 * Create a new donation record
 * 
 * @param donationData The donation data to create
 * @returns The created donation ID
 */
export async function createDonation(
  donationData: Omit<Donation, 'id' | 'createdAt'>
): Promise<string> {
  try {
    // Create donation record
    const docRef = await addDoc(collection(db, 'donations'), {
      ...donationData,
      createdAt: serverTimestamp()
    });
    
    // Note: Transaction creation is now handled server-side in the donation-process API
    // after successful payment processing to prevent race conditions
    
    return docRef.id;
  } catch (error) {
    console.error('Error creating donation:', error);
    throw new Error('Failed to create donation');
  }
}

/**
 * Get all donations for a company
 * 
 * @param companyId The company ID to get donations for
 * @returns Array of donations
 */
export async function getDonations(companyId: string): Promise<Donation[]> {
  try {
    const donationsRef = collection(db, 'donations');
    // Normalize companyId to include prefix for robustness
    const normalizedCompanyId = companyId?.startsWith('company_') ? companyId : `company_${companyId}`;

    // Simplified query without orderBy to avoid index requirement
    const q = query(
      donationsRef,
      where('companyId', '==', normalizedCompanyId)
    );

    const querySnapshot = await getDocs(q);
    const donations: Donation[] = [];

    querySnapshot.forEach((doc) => {
      const data = doc.data();
      donations.push({
        id: doc.id,
        ...data,
        createdAt: data.createdAt?.toDate() || new Date()
      } as Donation);
    });

    // Sort donations by createdAt in descending order after fetching
    donations.sort((a, b) => {
      const dateA = a.createdAt instanceof Date ? a.createdAt.getTime() : new Date(a.createdAt).getTime();
      const dateB = b.createdAt instanceof Date ? b.createdAt.getTime() : new Date(b.createdAt).getTime();
      return dateB - dateA; // descending order
    });

    return donations;
  } catch (error) {
    console.error('Error getting donations:', error);
    throw new Error('Failed to get donations');
  }
}

/**
 * Get donation settings for a company
 * 
 * @param companyId The company ID to get donation settings for
 * @returns The donation settings
 */
export async function getDonationSettings(companyId: string): Promise<DonationSettings | null> {
  try {
    const companyRef = doc(db, 'companies', companyId);
    const companySnap = await getDoc(companyRef);
    
    if (!companySnap.exists()) {
      return null;
    }
    
    const companyData = companySnap.data() as CompanySettings;
    
    // Determine the industry - try multiple ways
    let companyIndustry = 'none';
    
    // First check invoiceSettings.industry
    if (companyData.invoiceSettings?.industry && companyData.invoiceSettings.industry !== 'none') {
      companyIndustry = companyData.invoiceSettings.industry;
    } 
    // Then check general industry field
    else if (companyData.industry && companyData.industry !== 'none') {
      companyIndustry = companyData.industry;
    }
    // Special case for cruise ships - check company name
    else if (companyData.name) {
      // Check if company name contains cruise ship related keywords
      const nameLower = companyData.name.toLowerCase();
      if (
        nameLower.includes('cruise') || 
        nameLower.includes('ship') || 
        nameLower.includes('yacht') || 
        nameLower.includes('boat') ||
        nameLower.includes('sail') ||
        nameLower.includes('sea') ||
        nameLower.includes('vie') // for Villa Vie Residences
      ) {
        companyIndustry = 'cruise_ship';
      }
    }
    
    // If no donation settings, create default settings with industry-specific fields
    if (!companyData.donationSettings) {
      // Get industry-specific fields
      const industryFields = getIndustryFields(companyIndustry);
      
      // Create dynamic fields from industry fields
      const dynamicFields = industryFields.map(field => ({
        id: uuidv4(),
        name: field.name,
        label: field.label,
        type: field.type,
        required: field.required,
        options: field.options
      }));
      
      return {
        enabled: false,
        title: 'Support Our Organization',
        subtitle: 'Your donation makes a difference',
        description: 'Thank you for your support. Your donation helps us continue our important work.',
        slug: 'donate',
        industry: companyIndustry,
        showPresetAmounts: true,
        presetAmounts: [10, 25, 50, 100],
        platformFeeEnabled: companyData.platformFee ? true : false,
        dynamicFields: dynamicFields,
        thankYouRedirectUrl: '',
        thankYouMessage: 'Your generous support is greatly appreciated. A thank you email has been sent to your address.',
        // Default to collecting address for backwards compatibility
        collectAddress: true,
      };
    }
    
    // If donation settings exist but no dynamic fields or industry changed, update dynamic fields
    // BUT only if not in custom fields mode (for backwards compatibility)
    if (companyData.donationSettings && 
        !companyData.donationSettings.customFieldsMode && // Don't auto-update custom fields
        (!companyData.donationSettings.dynamicFields || 
         companyData.donationSettings.dynamicFields.length === 0 ||
         companyData.donationSettings.industry !== companyIndustry)) {
      
      // Get industry-specific fields
      const industryFields = getIndustryFields(companyIndustry);
      
      // Create dynamic fields from industry fields
      const dynamicFields = industryFields.map(field => ({
        id: uuidv4(),
        name: field.name,
        label: field.label,
        type: field.type,
        required: field.required,
        options: field.options
      }));
      
      // Update the settings with the new industry and dynamic fields
      const updatedSettings = {
        ...companyData.donationSettings,
        industry: companyIndustry,
        dynamicFields: dynamicFields
      };
      
      // Save the updated settings
      await updateDoc(companyRef, {
        'donationSettings': updatedSettings,
        updatedAt: serverTimestamp()
      });
      
      return updatedSettings;
    }
    
    // Ensure the returned settings include a default for collectAddress
    return {
      collectAddress: true,
      ...companyData.donationSettings,
    } as DonationSettings;
  } catch (error) {
    console.error('Error getting donation settings:', error);
    throw new Error('Failed to get donation settings');
  }
}

/**
 * Update donation settings for a company
 * 
 * @param companyId The company ID to update donation settings for
 * @param settings The donation settings to update
 */
export async function updateDonationSettings(
  companyId: string, 
  settings: DonationSettings
): Promise<void> {
  try {
    const companyRef = doc(db, 'companies', companyId);
    
    // First check if the company document exists and has a features object
    const companyDoc = await getDoc(companyRef);
    if (!companyDoc.exists()) {
      throw new Error('Company not found');
    }
    
    const companyData = companyDoc.data() as CompanySettings;
    
    // Deep clone and sanitize the settings object to remove any undefined values
    const sanitizedSettings = JSON.parse(JSON.stringify(settings));
    
    // Create update data with proper structure and sanitized values
    const updateData: any = {
      'donationSettings': sanitizedSettings,
      updatedAt: serverTimestamp()
    };
    
    // Ensure features object exists and set donations property
    if (companyData.features) {
      // Features object exists, just update the donations property
      updateData['features.donations'] = true;
    } else {
      // Features object doesn't exist, create it with donations enabled
      updateData['features'] = {
        donations: true,
        recurringInvoices: false,
        virtualTerminal: false,
        userManagement: false
      };
    }
    
    try {
      await updateDoc(companyRef, updateData);
    } catch (updateError) {
      console.error('Error in updateDoc operation:', updateError);
      throw updateError;
    }
  } catch (error) {
    console.error('Error in updateDonationSettings:', error);
    throw new Error('Failed to update donation settings');
  }
}

/**
 * Get a company by its name slug
 * 
 * @param companyNameSlug The company name slug from the URL
 * @returns The company ID and settings if found
 */
export async function getCompanyByDonationSlug(companyNameSlug: string): Promise<{
  companyId: string;
  companyName: string;
  donationSettings: DonationSettings;
} | null> {
  try {
    const companiesRef = collection(db, 'companies');
    
    // Normalize the company name slug from the URL
    const urlNameLower = companyNameSlug.toLowerCase();
    
    // Get all companies with donation settings enabled
    const q = query(
      companiesRef,
      where('donationSettings.enabled', '==', true)
    );
    
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      return null;
    }
    
    // First try exact match, then try permissive match as fallback
    let matchedCompany = null;
    
    // Find the company with matching name (trying different normalization methods)
    for (const doc of querySnapshot.docs) {
      const companyData = doc.data() as CompanySettings;
      
      if (!companyData.name) {
        continue;
      }
      
      // Try multiple normalization approaches
      const dbNameNormalized1 = companyData.name.toLowerCase().replace(/[^a-z0-9]/g, '');
      const dbNameNormalized2 = companyData.name.toLowerCase().replace(/\s+/g, '');
      const dbNameLower = companyData.name.toLowerCase();
      
      // Exact match attempt
      if (dbNameNormalized1 === urlNameLower || 
          dbNameNormalized2 === urlNameLower || 
          dbNameLower === urlNameLower) {
        return {
          companyId: doc.id,
          companyName: companyData.name || '',
          donationSettings: companyData.donationSettings!
        };
      }
      
      // Record partial match for fallback
      if (!matchedCompany) {
        // Partial match (URL contains company name or vice versa)
        if (urlNameLower.includes(dbNameLower) || 
            dbNameLower.includes(urlNameLower) ||
            dbNameNormalized1.includes(urlNameLower) || 
            urlNameLower.includes(dbNameNormalized1)) {
          matchedCompany = {
            companyId: doc.id,
            companyName: companyData.name || '',
            donationSettings: companyData.donationSettings!
          };
        }
      }
    }
    
    // Return partial match if found
    if (matchedCompany) {
      return matchedCompany;
    }
    
    // No matching company found
    return null;
  } catch (error) {
    console.error('Error getting company by donation slug:', error);
    throw new Error('Failed to get company by donation slug');
  }
}

/**
 * Get industry-specific dynamic fields for donation forms
 * 
 * @param industry The industry to get fields for
 * @returns Array of dynamic fields for the industry
 */
export function getIndustryFields(industry: string): {
  name: string;
  label: string;
  type: 'text' | 'number' | 'select';
  required: boolean;
  options?: string[];
}[] {
  // Define industry-specific fields
  const industryFields: Record<string, any[]> = {
    'cruise_ship': [
      {
        name: 'cabin_number',
        label: 'Cabin Number',
        type: 'text',
        required: true
      },
      {
        name: 'host_name',
        label: 'Host Name or Purpose of Payment',
        type: 'text',
        required: false
      }
    ],
    'religion': [
      {
        name: 'place_of_worship',
        label: 'Place of Worship',
        type: 'text',
        required: false
      },
      {
        name: 'fund',
        label: 'Fund',
        type: 'select',
        required: false,
        options: ['General Fund', 'Building Fund', 'Missions', 'Youth', 'Other']
      }
    ],
    'education': [
      {
        name: 'class',
        label: 'Class/Grade',
        type: 'text',
        required: false
      },
      {
        name: 'student_id',
        label: 'Student ID',
        type: 'text',
        required: false
      }
    ],
    'healthcare': [
      {
        name: 'patient_id',
        label: 'Patient ID',
        type: 'text',
        required: false
      }
    ],
    'nonprofit': [
      {
        name: 'campaign',
        label: 'Campaign',
        type: 'select',
        required: false,
        options: ['General', 'Annual Fund', 'Capital Campaign', 'Special Event', 'Other']
      },
      {
        name: 'dedication',
        label: 'Dedication',
        type: 'text',
        required: false
      }
    ]
  };
  
  // Return fields for the specified industry or empty array if industry not found
  return industryFields[industry] || [];
}

/**
 * Update a donation status after payment processing
 * 
 * @param donationId The donation ID to update
 * @param status The new status
 * @param transactionId Optional transaction ID for successful payments
 */
export async function updateDonationStatus(
  donationId: string,
  status: 'pending' | 'completed' | 'failed',
  transactionId?: string
): Promise<void> {
  try {
    const donationRef = doc(db, 'donations', donationId);
    const updateData: any = {
      status,
      updatedAt: serverTimestamp()
    };
    
    if (transactionId) {
      updateData.transactionId = transactionId;
    }
    
    await updateDoc(donationRef, updateData);
  } catch (error) {
    console.error('Error updating donation status:', error);
    throw new Error('Failed to update donation status');
  }
}

/**
 * Update a donation record with a customer ID
 * Links a donation to a customer for tracking purposes
 */
export async function updateDonationCustomerId(
  donationId: string,
  customerId: string
): Promise<void> {
  const donationRef = doc(db, 'donations', donationId);
  await updateDoc(donationRef, { customerId });
}

/**
 * Find or create a customer record from donation data
 * Deduplicates by email to avoid creating duplicate customer records
 *
 * @param params - Donor information (firstName, lastName, email, address, etc.)
 * @returns Customer ID if found or created, null if no email provided
 */
export async function findOrCreateDonorCustomer({
  companyId,
  firstName,
  lastName,
  email,
  address,
  city,
  state,
  zip,
  country
}: {
  companyId: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  address?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
}): Promise<string | null> {
  if (!email || !companyId) return null;

  const normalizedCompanyId = companyId.startsWith('company_') ? companyId : `company_${companyId}`;

  try {
    // Check if customer with this email already exists
    const customersRef = collection(db, 'customers');
    const q = query(
      customersRef,
      where('companyId', '==', normalizedCompanyId),
      where('email', '==', email)
    );
    const existing = await getDocs(q);

    if (!existing.empty) {
      return existing.docs[0].id;
    }

    // Create new customer record
    const now = serverTimestamp();
    const customerDoc = {
      firstName: firstName || '',
      lastName: lastName || '',
      email,
      phone: '',
      street: address || '',
      city: city || '',
      state: state || '',
      zip: zip || '',
      country: country || '',
      notes: '',
      status: 'active',
      paymentStatus: 'pending',
      companyId: normalizedCompanyId,
      createdAt: now,
      updatedAt: now,
    };

    const ref = await addDoc(customersRef, customerDoc);
    return ref.id;
  } catch (err) {
    console.error('[findOrCreateDonorCustomer] Error:', err);
    return null;
  }
}

/**
 * Restore dynamic field data for existing donation records
 * This helps fix issues where donation records don't have dynamic field data
 */
export async function restoreDonationDynamicFields(companyId: string): Promise<void> {
  try {
    console.log('Starting restoration of dynamic fields for donations...');
    
    // Get all donations for the company
    const donationsRef = collection(db, 'donations');
    const q = query(donationsRef, where('companyId', '==', companyId));
    const donationsSnapshot = await getDocs(q);
    
    // Get current donation settings to understand what fields should exist
    const donationSettings = await getDonationSettings(companyId);
    
    let updatedCount = 0;
    const batch = writeBatch(db);
    
    donationsSnapshot.docs.forEach((docSnap) => {
      const donation = docSnap.data() as Donation;
      
      // Check if donation is missing dynamic fields or has incomplete data
      if (!donation.dynamicFields || Object.keys(donation.dynamicFields).length === 0) {
        console.log(`Donation ${docSnap.id} missing dynamic fields, initializing...`);
        
        // Initialize empty dynamic fields object based on current settings
        const emptyDynamicFields: Record<string, string> = {};
        donationSettings.dynamicFields.forEach(field => {
          emptyDynamicFields[field.name] = '';
        });
        
        // Update the document with initialized dynamic fields
        batch.update(docSnap.ref, {
          dynamicFields: emptyDynamicFields,
          updatedAt: serverTimestamp()
        });
        
        updatedCount++;
      }
    });
    
    if (updatedCount > 0) {
      await batch.commit();
      console.log(`Updated ${updatedCount} donation records with dynamic fields structure`);
    } else {
      console.log('No donation records needed dynamic field restoration');
    }
    
  } catch (error) {
    console.error('Error restoring dynamic fields for donations:', error);
    throw new Error('Failed to restore dynamic fields');
  }
}

/**
 * Force update donation settings with proper cruise ship fields
 * This is a recovery function for when dynamic fields get misconfigured
 */
export async function forceCruiseShipFields(companyId: string): Promise<DonationSettings> {
  try {
    console.log('Force applying cruise ship fields...');
    
    const companyRef = doc(db, 'companies', companyId);
    const companyDoc = await getDoc(companyRef);
    
    if (!companyDoc.exists()) {
      throw new Error('Company not found');
    }
    
    const companyData = companyDoc.data() as CompanySettings;
    
    // Create cruise ship dynamic fields
    const cruiseShipFields: DynamicField[] = [
      {
        id: uuidv4(),
        name: 'cabin_number',
        label: 'Cabin Number',
        type: 'text',
        required: true,
        disabled: false
      },
      {
        id: uuidv4(),
        name: 'host_name',
        label: 'Host Name or Purpose of Payment',
        type: 'text',
        required: false,
        disabled: false
      }
    ];
    
    // Update donation settings with cruise ship fields
    const updatedSettings: DonationSettings = {
      ...companyData.donationSettings,
      industry: 'cruise_ship',
      dynamicFields: cruiseShipFields
    };
    
    // Save the updated settings
    await updateDoc(companyRef, {
      'donationSettings': updatedSettings,
      'invoiceSettings.industry': 'cruise_ship', // Also update main industry setting
      updatedAt: serverTimestamp()
    });
    
    console.log('Successfully applied cruise ship fields');
    return updatedSettings;
    
  } catch (error) {
    console.error('Error forcing cruise ship fields:', error);
    throw new Error('Failed to apply cruise ship fields');
  }
} 
